import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Select, Tag } from 'antd';
import useOnFetch from '@/hooks/useOnFetch';
import { request } from '@/request';

export default function ActionMultipleAutoCompleteAsync({
  entity,
  displayLabels,
  searchFields,
  outputValue = '_id',
  value,
  onChange,
  handleSave,
  disabled,
  fieldName = 'collaborator'
}) {
  const [selectOptions, setOptions] = useState([]);
  const [currentValue, setCurrentValue] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  const { onFetch, result, isSuccess, isLoading } = useOnFetch();
  const isUpdatingRef = useRef(true);
  const initialFetchDoneRef = useRef(false);

  const asyncSearch = async (options) => {
    try {
      const response = await request.search({ entity, options });
      return response;
    } catch (error) {
      console.error('Search failed:', error);
      return { success: false, result: [] };
    }
  };

  const labels = (optionField) => {
    return displayLabels.map((x) => optionField[x]).join(' ');
  };

  const fetchInitialValues = async (idsOrObjects) => {
    try {
      const ids = idsOrObjects.map((item) => {
        if (typeof item === 'object' && item !== null) {
          return item._id || item[outputValue];
        }
        return item;
      }).filter(Boolean);

      if (ids.length === 0) {
        setOptions([]);
        setCurrentValue([]);
        return;
      }

      const response = await request.get({ entity, ids });

      if (response.success && Array.isArray(response.result)) {
        const filteredUsers = response.result.filter((user) =>
          ids.includes(user._id || user[outputValue])
        );
        
        setOptions(prev => {
          const existingIds = new Set(prev.map(item => item._id || item[outputValue]));
          const newOptions = filteredUsers.filter(item => 
            !existingIds.has(item._id || item[outputValue])
          );
          return [...prev, ...newOptions];
        });
        
        const newValues = filteredUsers.map((item) => item._id || item[outputValue]);
        setCurrentValue(prev => {
          const allValues = [...prev, ...newValues];
          return [...new Set(allValues)];
        });
        initialFetchDoneRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching initial values:', error);
    }
  };

  // Handle initial value setup and external value changes
  useEffect(() => {
    if (value && isUpdatingRef.current) {
      let valueArray = [];
      
      if (Array.isArray(value)) {
        valueArray = value;
      } else if (value && typeof value === 'object') {
        valueArray = [value];
      }

      if (valueArray.length > 0) {
        const hasObjects = valueArray.some(item => typeof item === 'object' && item !== null);
        
        if (hasObjects) {
          const validObjects = valueArray.filter(item => 
            typeof item === 'object' && item !== null && (item.firstName || item._id)
          );
          
          if (validObjects.length > 0 && validObjects.every(item => item.firstName)) {
            setOptions(validObjects);
            setCurrentValue(validObjects.map(item => item._id || item[outputValue]));
            initialFetchDoneRef.current = true;
          } else {
            const ids = valueArray.map(item => item._id || item[outputValue] || item).filter(Boolean);
            if (!initialFetchDoneRef.current && ids.length > 0) {
              fetchInitialValues(ids);
            }
          }
        } else {
          const ids = valueArray.filter(Boolean);
          if (!initialFetchDoneRef.current && ids.length > 0) {
            fetchInitialValues(ids);
          }
        }
      } else {
        setCurrentValue([]);
        setOptions([]);
        initialFetchDoneRef.current = true;
      }
      isUpdatingRef.current = false;
    }
  }, [value, outputValue]);

  // Handle search results
  useEffect(() => {
    if (isSearching && isSuccess && Array.isArray(result)) {
      const selectedIds = new Set(currentValue.map(id => id?.toString()));
      const filteredResults = result.filter(
        (option) => !selectedIds.has((option._id || option[outputValue])?.toString())
      );
      setSearchResults(filteredResults);
      setIsSearching(false);
    } else if (isSearching) {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [isSuccess, result, outputValue, currentValue, isSearching]);

  // Handle search input
  const handleSearch = useCallback((searchText) => {
    if (searchText && searchText.trim() !== '') {
      setIsSearching(true);
      const options = {
        q: searchText.trim(),
        fields: searchFields,
      };
      onFetch(() => asyncSearch(options));
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [onFetch, searchFields]);

  // Handle selection change
  const handleChange = useCallback(async (newValue) => {
    setCurrentValue(newValue);

    const allOptions = [...selectOptions, ...searchResults];
    const selectedObjects = allOptions.filter(option => 
      newValue.includes(option._id || option[outputValue])
    );

    setOptions(prev => {
      const existingIds = new Set(prev.map(item => item._id || item[outputValue]));
      const newOptions = selectedObjects.filter(item => 
        !existingIds.has(item._id || item[outputValue])
      );
      return [...prev, ...newOptions];
    });

    if (onChange) {
      onChange(newValue);
    }

    if (handleSave && selectedObjects.length > 0) {
      try {
        const userIds = selectedObjects.map(obj => obj._id || obj[outputValue]);
        await handleSave(fieldName, userIds);
      } catch (error) {
        console.error('Error saving collaborators:', error);
      }
    }
    
    setSearchResults([]);
  }, [onChange, handleSave, selectOptions, searchResults, outputValue, fieldName]);

  // Handle dropdown visibility
  const handleDropdownVisibleChange = useCallback((open) => {
    setDropdownOpen(open);
    if (!open) {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, []);

  // Get option label
  const getOptionLabel = useCallback((option) => {
    return displayLabels.map((x) => option[x]).join(' ');
  }, [displayLabels]);

  // FIXED: Render selected items with proper close icon
  const renderTag = useCallback((props) => {
    const { label, closable, onClose, value } = props;
    
    // Find the full option object to get the display name
    const allOptions = [...selectOptions, ...searchResults];
    const option = allOptions.find(opt => 
      (opt._id || opt[outputValue]) === value
    );
    
    const displayLabel = option ? getOptionLabel(option) : label;
    
    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{
          background: '#f5f5f5',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          padding: '2px 8px',
          margin: '2px 4px 2px 0',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '14px',
          color: 'rgba(0, 0, 0, 0.85)',
        }}
      >
        {displayLabel}
      </Tag>
    );
  }, [selectOptions, searchResults, outputValue, getOptionLabel]);

  // Get display options
  const getDisplayOptions = useCallback(() => {
    const selectedIds = new Set(currentValue.map(id => id?.toString()));
    
    const selectedOptions = selectOptions.filter(opt => 
      selectedIds.has((opt._id || opt[outputValue])?.toString())
    );
    
    const availableSearchResults = searchResults.filter(opt => 
      !selectedIds.has((opt._id || opt[outputValue])?.toString())
    );
    
    return [...selectedOptions, ...availableSearchResults];
  }, [selectOptions, searchResults, currentValue, outputValue]);

  return (
    <Select
      mode="multiple"
      loading={isLoading}
      showSearch
      maxTagCount="responsive"
      allowClear
      placeholder="Search and select users..."
      defaultActiveFirstOption={false}
      showArrow={true}
      filterOption={false}
      notFoundContent={isSearching ? 'Searching...' : 'No users found'}
      value={currentValue}
      onSearch={handleSearch}
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      disabled={disabled}
      optionLabelProp="label"
      style={{ width: '100%' }}
      dropdownStyle={{ minWidth: '200px' }}
      tagRender={renderTag}
    >
      {getDisplayOptions().map((optionField) => {
        const optionValue = optionField._id || optionField[outputValue];
        const optionLabel = getOptionLabel(optionField);
        return (
          <Select.Option 
            key={optionValue} 
            value={optionValue}
            label={optionLabel}
          >
            {optionLabel}
          </Select.Option>
        );
      })}
    </Select>
  );
}